import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  MultiSelect,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconId,
  IconPlus,
  IconShieldLock,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { createUser, deleteUser, getUsers, updateUser } from "@/api/access";
import { getBranches } from "@/api/branches";
import { useAuthStore } from "@/stores/auth";
import { notify } from "@/lib/notify";
import {
  AREA_LABELS,
  AREA_OPTIONS,
  ROLE_LABELS,
  ROLE_OPTIONS,
} from "@/lib/permissions";
import {
  ListCell,
  ListEmpty,
  ListError,
  ListHeader,
  ListRow,
  ListScroll,
  ListSkeleton,
} from "@/components/DataList";

import { FormModalFooter } from "@/components/FormModalFooter";
import { FormSection } from "@/components/FormSection";
import { PageTitle } from "@/components/PageTitle";
import { EMPTY_LIST } from "@/lib/queryClient";

const USER_COLUMNS = [
  { label: "Ad", w: 180, ta: "left" },
  { label: "E-posta", flex: true },
  { label: "Rol", w: 140 },
  { label: "Şube", w: 70 },
  { label: "Ekran", w: 70 },
  { label: "İşlem", w: 160 },
];

const ALL_BRANCHES = "all";

const nextBranchSelection = (previous, next) => {
  const addedAll =
    next.includes(ALL_BRANCHES) && !previous.includes(ALL_BRANCHES);

  if (addedAll) {
    return [ALL_BRANCHES];
  }

  return next.length > 1
    ? next.filter((value) => value !== ALL_BRANCHES)
    : next;
};

const roleDescription = (role) =>
  ROLE_OPTIONS.find((option) => option.value === role)?.description ?? "";

function UserFormModal({ opened, onClose, user, branches }) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(user);

  const form = useForm({
    initialValues: {
      email: "",
      fullName: "",
      password: "",
      role: "member",
      branchIds: [],
      areas: [],
      status: "active",
    },
    validate: {
      fullName: (value) => (value.trim() ? null : "Ad soyad gerekli"),
      // E-posta yalnızca oluştururken giriliyor; düzenlemede alan kilitli.
      email: (value) =>
        isEdit || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
          ? null
          : "Geçerli e-posta gir",
      // Düzenlemede boş bırakmak "şifre değişmesin" demek; girildiyse yine 8 karakter şartı var.
      password: (value) => {
        if (!isEdit && value.length < 8) {
          return "En az 8 karakter";
        }
        return value && value.length < 8 ? "En az 8 karakter" : null;
      },
      branchIds: (value, values) =>
        values.role === "member" && !value.length ? "En az bir şube seç" : null,
      areas: (value, values) =>
        values.role === "member" && !value.length
          ? "En az bir ekran seç"
          : null,
    },
  });

  useEffect(() => {
    if (!opened) {
      return;
    }
    form.setValues({
      email: user?.email ?? "",
      fullName: user?.fullName ?? "",
      password: "",
      role: user?.role === "owner" ? "owner" : "member",
      branchIds: user?.allBranches
        ? [ALL_BRANCHES]
        : (user?.branchIds ?? []).map(String),
      areas: user?.areas ?? [],
      // GERÇEK durum: 'invited' burada 'active'e katlanınca, davetli bir kullanıcının
      // modalını açıp Kaydet'e basmak onu sessizce aktifleştiriyor ve bekleyen davet
      // linkini öldürüyordu (uç yalnızca hâlâ davetli hesaba şifre belirletiyor).
      status: user?.status ?? "active",
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, user]);

  const isMember = form.values.role === "member";

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        fullName: values.fullName.trim(),
        role: values.role,
        areas: isMember ? values.areas : [],
        allBranches: isMember && values.branchIds.includes(ALL_BRANCHES),
        branchIds:
          isMember && !values.branchIds.includes(ALL_BRANCHES)
            ? values.branchIds.map(Number)
            : [],
      };

      if (values.password) {
        payload.password = values.password;
      }
      if (isEdit) {
        // Dokunulmadıysa gönderilmiyor: her kaydetme durumu yeniden yazsaydı davet
        // bekleyen hesap, adı düzeltilirken aktifleşirdi.
        if (form.isDirty("status")) {
          payload.status = values.status;
        }

        return updateUser(user.id, payload);
      }

      return createUser({
        ...payload,
        email: values.email.trim(),
        password: values.password,
      });
    },
    onSuccess: () => {
      notify.success(
        isEdit ? "Kullanıcı güncellendi" : "Kullanıcı oluşturuldu",
      );
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "İşlem başarısız"),
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      size="lg"
      padding="lg"
      title={
        <Group gap="sm">
          <ThemeIcon variant="light" color="chocolate" radius="md" size="lg">
            <IconUser size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600}>
              {isEdit ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}
            </Text>
            <Text size="xs" c="dimmed">
              {isEdit
                ? "Bilgileri, rolü ve erişim durumunu güncelle"
                : "Panele erişecek yeni bir hesap aç"}
            </Text>
          </div>
        </Group>
      }
    >
      <form onSubmit={form.onSubmit((values) => mutation.mutate(values))}>
        <Stack gap="md">
          <FormSection
            icon={IconId}
            title="Hesap"
            description="Giriş bilgileri"
          >
            <Stack gap="sm">
              <SimpleGrid
                cols={{ base: 1, sm: 2 }}
                spacing="md"
                verticalSpacing="xs"
              >
                <TextInput
                  label="Ad Soyad"
                  placeholder="Ad Soyad"
                  {...form.getInputProps("fullName")}
                />
                <TextInput
                  label="E-posta"
                  type="email"
                  placeholder="ornek@marka.com"
                  disabled={isEdit}
                  {...form.getInputProps("email")}
                />
              </SimpleGrid>
              <PasswordInput
                label={isEdit ? "Yeni Şifre" : "Şifre"}
                description={
                  isEdit ? "Boş bırakırsan şifre değişmez" : "En az 8 karakter"
                }
                placeholder="••••••••"
                {...form.getInputProps("password")}
              />
            </Stack>
          </FormSection>

          <FormSection
            icon={IconShieldLock}
            title="Yetki"
            description="Rol, görebileceği şubeler ve ekranlar"
            action={
              isEdit ? (
                <Badge
                  variant="light"
                  radius="sm"
                  color={STATUS_META[form.values.status].color}
                >
                  {STATUS_META[form.values.status].label}
                </Badge>
              ) : null
            }
          >
            <Stack gap="sm">
              {/* Rolün ne yaptığı seçimin altında yazıyor — partner izin listesi okumak
                                zorunda kalmasın diye açıklama role bağlı değişiyor. */}
              <Select
                withCheckIcon={false}
                allowDeselect={false}
                label="Rol"
                description={roleDescription(form.values.role)}
                data={ROLE_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                {...form.getInputProps("role")}
              />

              {isMember && (
                <MultiSelect
                  withAsterisk
                  withCheckIcon={false}
                  searchable
                  clearable
                  label="Görebileceği Şubeler"
                  description="“Tüm şubeler” sonradan açılan şubeleri de kapsar; yeni şube açmak ve şube sırası gibi işlemler de açılır"
                  placeholder="Şube seç"
                  data={[
                    { value: ALL_BRANCHES, label: "Tüm şubeler" },
                    ...branches.map((branch) => ({
                      value: String(branch.id),
                      label: branch.name,
                    })),
                  ]}
                  {...form.getInputProps("branchIds")}
                  onChange={(next) =>
                    form.setFieldValue(
                      "branchIds",
                      nextBranchSelection(form.values.branchIds, next),
                    )
                  }
                />
              )}

              {isMember && (
                <Checkbox.Group
                  withAsterisk
                  label="Görebileceği Ekranlar"
                  description="Gördüğü ekranda her işlemi yapabilir"
                  {...form.getInputProps("areas")}
                >
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mt="xs">
                    {AREA_OPTIONS.map((area) => (
                      <Checkbox
                        key={area.value}
                        value={area.value}
                        label={area.label}
                        description={area.description}
                      />
                    ))}
                  </SimpleGrid>
                </Checkbox.Group>
              )}

              {isEdit && (
                <Select
                  withCheckIcon={false}
                  allowDeselect={false}
                  label="Durum"
                  description="Pasif hesap anında erişimini kaybeder"
                  // 'Davet bekliyor' yalnızca hesap o durumdayken listede: elle
                  // seçilebilir bir hâl değil, ama seçenek hiç olmayınca Select
                  // boş metin gösteriyordu.
                  data={[
                    ...(form.values.status === "invited"
                      ? [{ value: "invited", label: STATUS_META.invited.label }]
                      : []),
                    { value: "active", label: "Aktif" },
                    { value: "disabled", label: "Pasif" },
                  ]}
                  {...form.getInputProps("status")}
                />
              )}
            </Stack>
          </FormSection>

          <FormModalFooter>
            <Button variant="default" onClick={onClose}>
              Vazgeç
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isEdit ? "Kaydet" : "Oluştur"}
            </Button>
          </FormModalFooter>
        </Stack>
      </form>
    </Modal>
  );
}

// Durumun rengi ve etiketi tek yerde. 'invited' ürünün gerçek bir hâli oldu (davet akışı) ama
// listede de modalde de yalnızca 'disabled' biliniyordu; davet bekleyen hesap sorunsuz görünüyordu.
const STATUS_META = {
  active: { label: "Aktif", color: "teal" },
  invited: { label: "Davet bekliyor", color: "yellow" },
  disabled: { label: "Pasif", color: "gray" },
};

export default function Users() {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const {
    data: allUsers = EMPTY_LIST,
    isLoading,
    isError,
    refetch,
  } = useQuery({ queryKey: ["users"], queryFn: getUsers });
  const { data: branches = EMPTY_LIST } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranches,
  });

  // Kişi kendini bu listede görmez: kendi rolünü/durumunu buradan değiştirmesi zaten API'de
  // engelli, adını ve şifresini sağ üstteki Hesap Ayarları'ndan düzenliyor.
  const users = useMemo(
    () => allUsers.filter((user) => user.id !== currentUserId),
    [allUsers, currentUserId],
  );

  const [userOpened, userModal] = useDisclosure(false);
  const [editUser, setEditUser] = useState(null);

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      notify.success("Kullanıcı silindi");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Silinemedi"),
  });

  const confirmDeleteUser = (user) =>
    modals.openConfirmModal({
      title: "Kullanıcıyı sil",
      centered: true,
      children: (
        <Text size="sm">
          {user.fullName} kullanıcısı silinecek. Emin misiniz?
        </Text>
      ),
      labels: { confirm: "Sil", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteUserMutation.mutate(user.id),
    });

  const branchScopeLabel = (user) => {
    if (user.role === "owner" || user.allBranches) {
      return "Tümü";
    }
    return user.role === "member" ? (user.branchIds?.length ?? 0) : "—";
  };

  const areaScopeLabel = (user) => {
    if (user.role === "owner") {
      return "Tümü";
    }
    return user.role === "member" ? (user.areas?.length ?? 0) : "—";
  };

  return (
    <Stack gap="lg">
      <PageTitle>Kullanıcılar</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconUsers size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>Kullanıcılar</Title>
            <Text c="dimmed" size="sm">
              Ekip üyelerinin rolünü ve hangi şubeleri görebileceklerini belirle
            </Text>
          </div>
        </Group>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            setEditUser(null);
            userModal.open();
          }}
        >
          Kullanıcı Ekle
        </Button>
      </Group>

      <Card withBorder radius="md" p={0}>
        <ListScroll minWidth={720}>
          <ListHeader columns={USER_COLUMNS} />
          {isError && (
            <ListError title="Kullanıcılar alınamadı" onRetry={refetch} />
          )}
          {!isError && isLoading && (
            <ListSkeleton columns={USER_COLUMNS} rows={4} />
          )}
          {!isError && !isLoading && users.length === 0 && (
            <ListEmpty>
              Başka kullanıcı yok — Kullanıcı Ekle ile davet edebilirsin
            </ListEmpty>
          )}
          {users.map((user) => (
            <ListRow key={user.id}>
              <ListCell w={180} ta="left">
                <Group gap={6} wrap="nowrap">
                  <Text fw={500} truncate>
                    {user.fullName}
                  </Text>
                  {user.status !== "active" && (
                    <Badge
                      size="xs"
                      color={STATUS_META[user.status].color}
                      variant="light"
                    >
                      {STATUS_META[user.status].label}
                    </Badge>
                  )}
                </Group>
              </ListCell>
              <ListCell flex>
                <Text size="sm" c="dimmed" truncate>
                  {user.email}
                </Text>
              </ListCell>
              <ListCell w={140}>
                <Badge
                  variant="light"
                  color={user.role === "owner" ? "grape" : "blue"}
                >
                  {ROLE_LABELS[user.role] ?? user.role}
                </Badge>
              </ListCell>
              <ListCell w={70}>
                <Text size="sm">{branchScopeLabel(user)}</Text>
              </ListCell>
              <ListCell w={70}>
                <Text
                  size="sm"
                  title={(user.areas ?? [])
                    .map((area) => AREA_LABELS[area] ?? area)
                    .join(", ")}
                >
                  {areaScopeLabel(user)}
                </Text>
              </ListCell>
              <ListCell w={160} ta="right">
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <Button
                    size="compact-sm"
                    variant="default"
                    onClick={() => {
                      setEditUser(user);
                      userModal.open();
                    }}
                  >
                    Düzenle
                  </Button>
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="red"
                    onClick={() => confirmDeleteUser(user)}
                  >
                    Sil
                  </Button>
                </Group>
              </ListCell>
            </ListRow>
          ))}
        </ListScroll>
      </Card>

      <UserFormModal
        opened={userOpened}
        onClose={userModal.close}
        user={editUser}
        branches={branches}
      />
    </Stack>
  );
}
